import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import CampusHeader from '../../../components/feature/CampusHeader';
import Footer from '../../../components/feature/Footer';
import { subscribeAuth, signInWithEmail, getUserRole, sendPasswordReset } from '../../../services/auth';
import { toUserMessage } from '../../../utils/errors';
import { reload } from 'firebase/auth';

const SignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState<string | null>(null);

  useEffect(() => {
    const unsub = subscribeAuth(async (user) => {
      if (!user) return;
      // Firebase caches emailVerified on the user object. If the user verified in
      // another tab/email client, this can lie. Refresh from the server first.
      try { await reload(user); } catch {}
      if (!user.emailVerified) {
        navigate('/auth/email-verification', { replace: true });
        return;
      }
      const role = await getUserRole(user.uid);
      if (redirectParam) {
        navigate(redirectParam);
        return;
      }
      if (role === 'seller') navigate('/seller-dashboard');
      else if (role === 'buyer') navigate('/buyer-dashboard');
      else navigate('/pending-verification');
    });
    return () => unsub();
  }, [navigate, redirectParam]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.email || !form.password) {
      setError('Please fill all required fields.');
      return;
    }
    setLoading(true);
    try {
      await signInWithEmail(form.email.trim(), form.password.trim());
      // Redirection handled by useEffect subscribeAuth
    } catch (err: any) {
      setError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <CampusHeader />
      <main className="max-w-md mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign In to your account</h1>
            <p className="text-gray-600">Welcome back! Please enter your details.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                autoComplete="username email"
                inputMode="email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="your.email@university.edu"
                required
              />
            </div>
            <div className="text-right">
              <button type="button" onClick={() => navigate('/auth/forgot-password')} className="text-sm text-blue-600 hover:text-blue-700">
                Forgot password?
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <i className={`ri-${showPwd ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
                </button>
              </div>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  key={error}
                  initial={{ opacity: 0, y: -8, x: 0 }}
                  animate={{ opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ x: { duration: 0.45 }, opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
                  className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
                >
                  <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                  <span className="font-medium">{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Don't have an account?{' '}
            <button onClick={() => navigate('/auth/sign-up')} className="text-blue-600 hover:text-blue-700 cursor-pointer">Sign Up</button>
          </p>
        </div>
      </main>

      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
              <button onClick={() => setShowResetModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Enter your account email to receive a password reset link.</p>
            <div className="space-y-4">
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <AnimatePresence>
                {resetStatus && (
                  <motion.p
                    key={resetStatus}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`text-sm font-medium ${/sent|check/i.test(resetStatus) ? 'text-green-700' : 'text-red-700'}`}
                  >
                    {resetStatus}
                  </motion.p>
                )}
              </AnimatePresence>
              <button
                onClick={async () => {
                  setResetStatus(null);
                  try {
                    await sendPasswordReset(resetEmail.trim());
                    setResetStatus('Reset email sent. Check your inbox.');
                  } catch (e: any) {
                    setResetStatus(toUserMessage(e));
                  }
                }}
                className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Send Reset Link
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SignInPage;
